import {
  Badge,
  Button,
  Card,
  Flex,
  Skeleton,
  Tag,
  theme,
  Typography
} from 'antd'
import {
  Check,
  Database,
  LayoutGrid,
  Lock,
  Minus,
  Plus,
  ShieldCheck,
  type LucideIcon
} from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { createElement, type ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AvatarWithInitials, SectionLabel } from 'src/components'
import { paths } from 'src/router'

import {
  AT,
  COUNTER_SLOT_HEIGHT,
  EASE,
  fadeIn,
  panelIn,
  popIn,
  riseIn,
  rowIn,
  slideRight,
  stepDelay,
  swap
} from './constants'
import { PulseDot } from './PulseDot'
import { useStyles } from './styles'
import type {
  BuildActivity,
  BuildStepKind,
  BuildStepStatus
} from './useBuildActivity'

const { Text } = Typography

const HEADER_AVATAR_SIZE = 34

/**
 * The shared eyebrow label uppercases itself for dense table headers. This panel
 * reads as prose beside a summary, so it keeps the copy as written — overridden
 * here rather than in `SectionLabel`, which the rest of the app still wants.
 */
const SENTENCE_CASE = { textTransform: 'none' } as const

/** An answer the flow collected, reported back with the icon it was picked by. */
export type BuildFacet = {
  label: string
  Icon: ComponentType<{ size?: number; color?: string }>
}

type Props = {
  workspaceName: string
  workspaceId: number
  workspaceUrl: string
  photoUrl?: string
  memberCount: number
  invitesSent: number
  activity: BuildActivity
  /** Which industry the workspace was seeded from, when one was chosen. */
  industry?: BuildFacet
  /** The "main priority" answer. */
  priority?: BuildFacet
}

/** One icon per category, in the order the design gives them. */
const KIND_ICONS: Record<BuildStepKind, LucideIcon> = {
  roles: ShieldCheck,
  signIn: Lock,
  apps: LayoutGrid,
  demoData: Database
}

type GlyphProps = { status: BuildStepStatus; className: string }

const StepGlyph = ({ status, className }: GlyphProps) => (
  <span className={className}>
    {status === 'done' ? (
      <Check size={14} color="var(--ant-color-primary)" />
    ) : status === 'skipped' ? (
      <Minus size={14} color="var(--ant-color-text-quaternary)" />
    ) : (
      <Badge status={status === 'running' ? 'processing' : 'default'} />
    )}
  </span>
)

/**
 * Right pane of the done step: the workspace being provisioned, reported live.
 *
 * Carries the same floating-card treatment as the preview mock the earlier steps
 * show, so the pane reads as one object continuing rather than as a new panel
 * appearing — the mock stops being a mock and starts being the workspace.
 *
 * Everything is `useSetupProgress`, nothing is invented: the rows are the
 * pipeline's own steps, the log is the ones that already reported, and the
 * trailing shimmer stands for the ones that have not.
 *
 * The blocks enter on their own beats, but a row that arrives later — a step
 * finishing while somebody watches — is only staggered against its own siblings,
 * so a live arrival lands at once instead of waiting out the opening sequence.
 */
export const BuildActivityPanel = ({
  workspaceName,
  workspaceId,
  workspaceUrl,
  photoUrl,
  memberCount,
  invitesSent,
  activity,
  industry,
  priority
}: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyles()
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()

  const animates = !reduceMotion

  const stepName = (kind: BuildStepKind): string =>
    t(`workspaces.onboarding.done.build.stepNames.${kind}`)

  const tone = activity.hasFailedStep
    ? 'error'
    : activity.canOpenWorkspace
      ? 'success'
      : 'processing'

  const liveLabel = activity.hasFailedStep
    ? t('workspaces.onboarding.done.build.failed')
    : activity.canOpenWorkspace
      ? t('workspaces.onboarding.done.build.ready')
      : activity.runningKind
        ? stepName(activity.runningKind)
        : t('workspaces.onboarding.done.build.starting')

  const counterShift = invitesSent > 0 ? -COUNTER_SLOT_HEIGHT : 0

  return (
    <div className={styles.activityPane}>
      <motion.div
        className={styles.activityShell}
        {...panelIn(animates, AT.panel)}
      >
        <Card
          variant="borderless"
          className={styles.activityCard}
          styles={{ body: { padding: 0 } }}
        >
          {/* The header's own class rides the wrapper, not the Flex inside it:
              it sticks to the top of the scrolling body, and a sticky element
              can only travel within its parent's box — pinned to a wrapper of
              exactly its own height it would have nowhere to go. */}
          <motion.div
            className={styles.activityHeader}
            {...fadeIn(animates, AT.panelHeader)}
          >
            <Flex align="center" gap={token.marginSM}>
              {/* Name only, no id — the same tile the preview drew. */}
              <AvatarWithInitials
                name={workspaceName}
                src={photoUrl}
                size={HEADER_AVATAR_SIZE}
                initialsFontSize={token.fontSize}
              />

              <Flex
                vertical
                gap={token.marginXXS / 2}
                className={styles.rowLabel}
              >
                <Text strong ellipsis>
                  {workspaceName}
                </Text>
                <span className={styles.domainLine}>{workspaceUrl}</span>
              </Flex>

              <Tag variant="filled" color={tone}>
                <Flex align="center" gap={token.marginXXS}>
                  <PulseDot
                    animates={animates && !activity.canOpenWorkspace}
                    tone={activity.hasFailedStep ? 'error' : 'primary'}
                  />
                  <AnimatePresence mode="wait">
                    <motion.span key={liveLabel} {...swap(animates)}>
                      {liveLabel}
                    </motion.span>
                  </AnimatePresence>
                </Flex>
              </Tag>
            </Flex>
          </motion.div>

          <motion.div
            className={styles.activityBlock}
            {...riseIn(animates, AT.panelMembers)}
          >
            <Flex align="flex-start" gap={token.marginLG} wrap="wrap">
              <Flex vertical gap={token.marginXXS}>
                <SectionLabel style={SENTENCE_CASE}>
                  {t('workspaces.onboarding.done.build.members')}
                </SectionLabel>

                <Flex align="center" gap={token.marginXS}>
                  <div className={styles.counterWindow}>
                    <motion.div
                      initial={{ y: animates ? 0 : counterShift }}
                      animate={{ y: counterShift }}
                      transition={{
                        duration: animates ? 0.7 : 0,
                        ease: EASE,
                        delay: animates ? AT.memberRoll : 0
                      }}
                    >
                      <div className={styles.counterSlot}>
                        {memberCount - invitesSent}
                      </div>
                      {invitesSent > 0 ? (
                        <div
                          className={cx(
                            styles.counterSlot,
                            styles.counterSlotArrived
                          )}
                        >
                          {memberCount}
                        </div>
                      ) : null}
                    </motion.div>
                  </div>

                  {invitesSent > 0 ? (
                    <motion.span {...popIn(animates, AT.memberPill)}>
                      <Tag variant="filled" color="success">
                        {`+${invitesSent}`}
                      </Tag>
                    </motion.span>
                  ) : null}
                </Flex>
              </Flex>

              {/* The two answers the flow asked for, reported back with the
                  icon they were picked by — the summary beside this panel says
                  what was built, these say what it was built from. */}
              {industry ? (
                <Flex vertical gap={token.marginXXS}>
                  <SectionLabel style={SENTENCE_CASE}>
                    {t('workspaces.onboarding.done.build.industry')}
                  </SectionLabel>
                  <Flex align="center" gap={token.marginXS}>
                    <span className={styles.facetIcon}>
                      {createElement(industry.Icon, { size: 14 })}
                    </span>
                    <Text>{industry.label}</Text>
                  </Flex>
                </Flex>
              ) : null}

              {priority ? (
                <Flex vertical gap={token.marginXXS}>
                  <SectionLabel style={SENTENCE_CASE}>
                    {t('workspaces.onboarding.done.build.priority')}
                  </SectionLabel>
                  <Flex align="center" gap={token.marginXS}>
                    <span className={styles.facetIcon}>
                      {createElement(priority.Icon, { size: 14 })}
                    </span>
                    <Text>{priority.label}</Text>
                  </Flex>
                </Flex>
              ) : null}
            </Flex>
          </motion.div>

          <div className={styles.activityBlock}>
            <Flex vertical gap={token.marginXXS}>
              <motion.div {...fadeIn(animates, AT.firstPanelStep - 0.1)}>
                <SectionLabel style={SENTENCE_CASE}>
                  {t('workspaces.onboarding.done.build.steps')}
                </SectionLabel>
              </motion.div>

              <div>
                <AnimatePresence initial={animates}>
                  {activity.steps.map((step, index) => (
                    <motion.div
                      key={step.kind}
                      className={styles.dividedRow}
                      {...slideRight(
                        animates,
                        stepDelay(AT.firstPanelStep, AT.panelStepGap, index)
                      )}
                    >
                      <Flex align="center" gap={token.marginSM}>
                        <StepGlyph
                          status={step.status}
                          className={styles.glyph}
                        />
                        <Text
                          className={cx(
                            styles.rowLabel,
                            step.status === 'skipped' && styles.mutedRow
                          )}
                          ellipsis
                        >
                          {stepName(step.kind)}
                        </Text>
                        <span className={styles.rowState}>
                          <AnimatePresence mode="wait">
                            <motion.span
                              key={step.status}
                              {...swap(animates)}
                              style={{ display: 'inline-block' }}
                            >
                              {t(
                                `workspaces.onboarding.done.build.status.${step.status}`
                              )}
                            </motion.span>
                          </AnimatePresence>
                        </span>
                      </Flex>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {activity.isAwaitingSteps ? (
                  <motion.div {...fadeIn(animates, AT.firstPanelStep)}>
                    <Skeleton
                      active
                      title={false}
                      paragraph={{ rows: 3, width: ['62%', '48%', '55%'] }}
                    />
                  </motion.div>
                ) : null}
              </div>
            </Flex>
          </div>

          <div className={styles.activityBlock}>
            <Flex vertical gap={token.marginXXS}>
              <motion.div {...fadeIn(animates, AT.firstPanelLog - 0.15)}>
                <SectionLabel style={SENTENCE_CASE}>
                  {t('workspaces.onboarding.done.build.activity')}
                </SectionLabel>
              </motion.div>

              <div>
                <AnimatePresence initial={animates}>
                  {activity.settledNewestFirst.map((step, index) => (
                    <motion.div
                      key={step.kind}
                      className={styles.dividedRow}
                      {...rowIn(animates, stepDelay(0, AT.panelLogGap, index))}
                    >
                      <Flex align="center" gap={token.marginSM}>
                        <span className={styles.logGlyph}>
                          {createElement(KIND_ICONS[step.kind], { size: 13 })}
                        </span>
                        <Text className={styles.rowLabel} ellipsis>
                          {stepName(step.kind)}
                        </Text>
                        {/* The status, not a clock. Every row here is a
                            category that finished, so it says so — the same
                            word the checklist above uses. A time would have to
                            come from the pipeline's own steps; measured on this
                            side it dated from the page opening, not from the
                            run, and then crept upwards while being read. */}
                        <span className={styles.rowState}>
                          {t('workspaces.onboarding.done.build.status.done')}
                        </span>
                      </Flex>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {activity.demoDataSkipped ? (
                  <motion.div
                    className={styles.dividedRow}
                    {...rowIn(
                      animates,
                      stepDelay(
                        0,
                        AT.panelLogGap,
                        activity.settledNewestFirst.length
                      )
                    )}
                  >
                    <Flex align="center" gap={token.marginSM}>
                      <span
                        className={cx(styles.logGlyph, styles.logGlyphMuted)}
                      >
                        <Database size={13} />
                      </span>
                      <Text className={cx(styles.rowLabel, styles.mutedRow)}>
                        {t('workspaces.onboarding.done.build.demoSkipped')}
                      </Text>
                      <span className={styles.rowState}>
                        {t('workspaces.onboarding.done.build.byYourChoice')}
                      </span>
                    </Flex>
                  </motion.div>
                ) : null}

                {activity.canOpenWorkspace ? null : (
                  <motion.div
                    className={styles.dividedRow}
                    {...fadeIn(animates, AT.panelPending)}
                  >
                    <Flex align="center" gap={token.marginSM}>
                      <div className={styles.rowLabel}>
                        <Skeleton
                          active
                          avatar={{
                            size: token.controlHeightSM,
                            shape: 'square'
                          }}
                          title={false}
                          paragraph={{ rows: 1, width: '58%' }}
                        />
                      </div>
                      <span className={styles.rowState}>
                        {t('workspaces.onboarding.done.build.writing')}
                      </span>
                    </Flex>
                  </motion.div>
                )}
              </div>
            </Flex>
          </div>

          {/* A workspace seeded with nothing looks broken unless the screen says
              it is empty on purpose and offers the first thing to do about it. */}
          {activity.demoDataSkipped ? (
            <motion.div
              className={styles.emptyCallout}
              {...riseIn(animates, AT.emptyState)}
            >
              <Flex align="center" gap={token.marginSM}>
                <span className={styles.logGlyph}>
                  <Plus size={14} />
                </span>

                <Flex vertical gap={2} className={styles.rowLabel}>
                  <Text strong>
                    {t('workspaces.onboarding.done.build.emptyTitle')}
                  </Text>
                  <Text type="secondary" className={styles.emptyBody}>
                    {t('workspaces.onboarding.done.build.emptyBody')}
                  </Text>
                </Flex>

                <Button
                  size="small"
                  onClick={() =>
                    navigate(paths.workspaces.apps.root(String(workspaceId)))
                  }
                >
                  {t('workspaces.onboarding.done.build.emptyAction')}
                </Button>
              </Flex>
            </motion.div>
          ) : null}
        </Card>
      </motion.div>
    </div>
  )
}
